import { useNavigation } from "@react-navigation/native";
import moment from "moment";
import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View, ViewProps } from "react-native";
import { useSelector } from "react-redux";
import { SLImage } from "src/components";

import * as connectionDb from "src/db/connection.db";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdDateRangeInput } from "src/components/forms/input";
import { PdStack, PdText } from "src/components/layout";
import { useFocusableQuery } from "src/hooks/api";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { getRepStats, getRepStatsMonthlySales, getRepStatsSalesSnapshot } from "src/util/apiNew";
import { formatMoney, formatPercentage } from "src/util/gFormatting";

import { PdCard } from "./card";
import { SalesLineChart } from "./charts";
import RecentOrdersTable from "./RecentOrdersTable";

const MonthlySalesCard: React.FC<ViewProps> = ({ style }) => {
  const isMobile = useIsMobile();
  const [period, setPeriod] = React.useState([moment().subtract(6, "month").toDate(), moment().toDate()]);
  const query = useFocusableQuery(
    React.useCallback(
      () =>
        getRepStatsMonthlySales({
          start: moment(period[0]).startOf("month").format("YYYY-MM"),
          end: moment(period[1]).endOf("month").format("YYYY-MM"),
        }),
      [period],
    ),
  );

  const onChange = (dates: [Date, Date]) => {
    if (Array.isArray(dates) && dates[0] && dates[1]) {
      setPeriod(dates);
    }
  };

  return (
    <PdCard style={[styles.zIndex1, style]}>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        style={[styles.zIndex1, styles.mb4, isMobile ? [] : [styles.alignCenter, styles.justifyBetween]]}
      >
        <PdText style={[styles.fontMedium, styles.textLg]}>Monthly Sales</PdText>
        <View style={[styles.flexRow, styles.alignCenter]}>
          <PdFormLabel style={styles.mr3}>Date:</PdFormLabel>
          <PdDateRangeInput
            containerStyle={isMobile ? styles.flex1 : styles.widthMd}
            style={styles.textCenter}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            period={[period[0], period[1]]}
            startDate={period[0]}
            endDate={period[1]}
            onChange={onChange}
          />
        </View>
      </PdStack>
      <SalesLineChart data={query.data} />
    </PdCard>
  );
};

const SalesSnapshotCard: React.FC<ViewProps> = ({ style }) => {
  const isMobile = useIsMobile();
  const [period, setPeriod] = React.useState([moment().subtract(1, "month").toDate(), moment().toDate()]);
  const query = useFocusableQuery(
    React.useCallback(
      () =>
        getRepStatsSalesSnapshot({
          start: moment(period[0]).format("YYYY-MM-DD"),
          end: moment(period[1]).format("YYYY-MM-DD"),
        }),
      [period],
    ),
  );

  const onChange = (dates: [Date, Date]) => {
    if (Array.isArray(dates) && dates[0] && dates[1]) setPeriod(dates);
  };

  return (
    <PdCard variant="solid" style={style}>
      <View style={[styles.flexRow, styles.alignCenter, styles.mlAuto, styles.mb4, styles.zIndex1]}>
        <PdFormLabel style={styles.mr3}>Date:</PdFormLabel>
        <PdDateRangeInput
          containerStyle={styles.flex1}
          style={[styles.textCenter, styles.textWhite]}
          dateFormat="MM/dd/yyyy"
          period={[period[0], period[1]]}
          startDate={period[0]}
          endDate={period[1]}
          onChange={onChange}
        />
      </View>
      <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite, styles.mb4]}>Sales Snapshot</PdText>
      <PdStack gap={4} layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"} style={styles.mb4}>
        <View>
          <PdFormLabel>Total Sales</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
            {formatMoney(query.data?.totalSales ?? 0)}
          </PdText>
          <PdText style={[styles.fontMedium, styles.textSm, styles.textWhite]}>{`${
            query.data?.totalSalesOrders ?? 0
          } ORDERS`}</PdText>
        </View>
        <View>
          <PdFormLabel>Promotional Sales</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
            {formatMoney(query.data?.promotionalSales ?? 0)}
          </PdText>
          <PdText style={[styles.fontMedium, styles.textSm, styles.textWhite]}>{`${
            query.data?.promotionalSalesOrders ?? 0
          } ORDERS`}</PdText>
        </View>
      </PdStack>
      <View>
        <PdFormLabel>Conversion Rate</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
          {formatPercentage(query.data?.conversionRate ?? 0)}
        </PdText>
        <PdText style={[styles.fontMedium, styles.textXs, styles.textWhite]}>
          Calculation based on active retailer connections.
        </PdText>
      </View>
    </PdCard>
  );
};

const RepDashboard: React.FC = () => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rCompanyName = useSelector((state) => state.CompanyReducer.type === "brand" && state.CompanyReducer.name);
  const query = useFocusableQuery(getRepStats);

  const handleOnCreateOrder = async () => {
    const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompanyId }).get();
    if (connectionsQuery.size > 1) {
      navigation.navigate(kRoute.P99);
    } else if (connectionsQuery.docs[0]) {
      navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
    }
  };

  // const handleOnViewAbandonedCarts = () => {
  //   navigation.navigate(kRoute.P03_03);
  // };

  return (
    <ScrollView contentContainerStyle={isMobile ? styles.p5 : styles.p10}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[2, 1]} gap={4} style={styles.mb4}>
        <MonthlySalesCard />
        <SalesSnapshotCard />
      </PdStack>

      <PdCard style={styles.mb4}>
        <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Recent Orders</PdText>
        <RecentOrdersTable type="rep" companyId={rCompanyId} />
      </PdCard>

      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1, 1]} gap={4} style={styles.mb4}>
        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Created Orders</PdText>
          <View style={styles.mb4}>
            <PdFormLabel>Total Sales</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.createdOrdersTotalSalesAmount ?? 0)}
            </PdText>
          </View>
          <View style={styles.mb4}>
            <PdFormLabel>Conversion Rate</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatPercentage(query.data?.createdOrdersConversionRate ?? 0)}
            </PdText>
          </View>
          <PdButton onPress={handleOnCreateOrder} style={styles.mrAuto}>
            <PdButtonLabel>Create an Order</PdButtonLabel>
          </PdButton>
        </PdCard>
        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Abandoned Carts</PdText>
          <View style={styles.mb4}>
            <PdFormLabel>Total Abandoned Carts</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>{query.data?.abandonedCartsQuantity ?? 0}</PdText>
          </View>
          <View
          // style={styles.mb4}
          >
            <PdFormLabel>Total Abandoned Carts Value</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.abandonedCartsTotalAmount ?? 0)}
            </PdText>
          </View>
          {/* <PdButton onPress={handleOnViewAbandonedCarts} style={styles.mrAuto}>
            <PdButtonLabel>View Abandoned Carts</PdButtonLabel>
          </PdButton> */}
        </PdCard>
      </PdStack>

      {(query.data?.bestSellersProducts?.length ?? 0) > 0 && (
        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Best Sellers</PdText>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={4} style={styles.justifyBetween}>
            {query.data?.bestSellersProducts.map((product, index) => (
              <TouchableOpacity
                key={product.id}
                onPress={() => navigation.navigate(kRoute.P101, { brand: rCompanyName, productId: product.id })}
                style={[styles.alignCenter, isMobile ? [] : styles.flex1]}
              >
                <SLImage
                  mobileStyle={styles.placeholderPortrait}
                  webStyle={webStyles.placeholderPortrait}
                  source={{ uri: product.imageUrl }}
                />
                <PdFormLabel style={[styles.textCenter, styles.mt1]}>{`#${index + 1}`}</PdFormLabel>
                <PdText style={[styles.textCenter, styles.mt1]}>{product.name}</PdText>
              </TouchableOpacity>
            ))}
          </PdStack>
        </PdCard>
      )}
    </ScrollView>
  );
};

export default RepDashboard;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  fontMedium: {
    fontWeight: "400",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt1: {
    marginTop: 1 * kStyleConst.kSpacing,
  },
  p10: {
    padding: kStyleConst.kSpacing * 10,
  },
  p5: {
    padding: kStyleConst.kSpacing * 5,
  },
  placeholderPortrait: {
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 80,
    width: 80,
  },
  textCenter: {
    textAlign: "center",
  },
  textLg: {
    fontSize: 22,
  },
  textSm: {
    fontSize: 12,
  },
  textWhite: {
    color: kColors.white,
  },
  textXs: {
    fontSize: 10,
  },
  widthMd: {
    width: 190,
  },
  zIndex1: {
    zIndex: 1,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  placeholderPortrait: {
    height: 80,
    width: 80,
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
  },
} as const;
